import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'

import { useOrders } from '../hooks/useOrders'
import { theme } from '../styles/theme'
import { shortenOrderNumber } from '../utils/orderNumber'

const fulfillmentOptions = [
  { value: '', label: 'Toate statusurile' },
  { value: 'new', label: 'Nouă' },
  { value: 'supplier_waiting', label: 'În așteptare furnizor' },
  { value: 'processing', label: 'În procesare' },
  { value: 'out_for_delivery', label: 'În livrare' },
  { value: 'completed', label: 'Finalizată' },
  { value: 'cancelled', label: 'Anulată' }
]

const paymentOptions = [
  { value: '', label: 'Toate plățile' },
  { value: 'awaiting_payment', label: 'În așteptarea plății' },
  { value: 'paid', label: 'Plătită' },
  { value: 'payment_failed', label: 'Plata a eșuat' },
  { value: 'refunded', label: 'Rambursată' }
]

const readinessColors = {
  all_in_stock: 'success',
  mixed: 'warning',
  supplier_only: 'default'
}

const readinessLabels = {
  all_in_stock: 'Toate în stoc',
  mixed: 'Mixt',
  supplier_only: 'Stoc furnizor'
}

const paymentLabels = {
  awaiting_payment: 'În așteptarea plății',
  paid: 'Plătită',
  payment_failed: 'Plata a eșuat',
  refunded: 'Rambursată'
}

const fulfillmentLabels = {
  new: 'Nouă',
  supplier_waiting: 'În așteptare furnizor',
  processing: 'În procesare',
  out_for_delivery: 'În livrare',
  completed: 'Finalizată',
  cancelled: 'Anulată'
}

const bankTransferLabels = {
  not_applicable: 'Nu se aplică',
  awaiting_proof: 'Așteaptă dovada',
  proof_uploaded: 'Dovada trimisă',
  confirmed: 'Plata confirmată',
  rejected: 'Dovada respinsă'
}

const Orders = () => {
  const { fetchOrders, getOrder, updateOrder, resendConfirmation, uploadFinalInvoice } = useOrders()
  const [orders, setOrders] = useState([])
  const [filters, setFilters] = useState({ search: '', payment_status: '', fulfillment_status: '' })
  const [showClosedOrders, setShowClosedOrders] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [statusDraft, setStatusDraft] = useState('new')
  const [notesDraft, setNotesDraft] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [actionSeverity, setActionSeverity] = useState('success')
  const [rowStatusDrafts, setRowStatusDrafts] = useState({})
  const [rowActionOrderId, setRowActionOrderId] = useState('')
  const [invoiceUploading, setInvoiceUploading] = useState(false)

  const buildAppliedFilters = (nextFilters = filters, nextShowClosedOrders = showClosedOrders) => {
    const hasSearch = Boolean((nextFilters.search || '').trim())
    return {
      ...nextFilters,
      only_open: !nextShowClosedOrders && !nextFilters.fulfillment_status && !hasSearch ? 'true' : ''
    }
  }

  const loadOrders = async (nextFilters = filters, nextShowClosedOrders = showClosedOrders) => {
    setLoading(true)
    setError('')
    try {
      const payload = await fetchOrders(buildAppliedFilters(nextFilters, nextShowClosedOrders))
      const nextOrders = payload.data || []
      setOrders(nextOrders)
      setRowStatusDrafts(currentDrafts => (
        nextOrders.reduce((drafts, order) => {
          drafts[order._id] = currentDrafts[order._id] || order.fulfillment_status || 'new'
          return drafts
        }, {})
      ))
    } catch (loadError) {
      setError(loadError.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [])

  const openDetails = async (orderId) => {
    setActionMessage('')
    try {
      const payload = await getOrder(orderId)
      setSelectedOrder(payload.data)
      setStatusDraft(payload.data.fulfillment_status || 'new')
      setNotesDraft(payload.data.notes || '')
      setDetailsOpen(true)
    } catch (loadError) {
      setError(loadError.message)
    }
  }

  const handleFilterChange = (field, value) => {
    const nextFilters = { ...filters, [field]: value }
    setFilters(nextFilters)
  }

  const applyFilters = () => {
    loadOrders(filters, showClosedOrders)
  }

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      applyFilters()
    }
  }

  const handleStatusSave = async () => {
    if (!selectedOrder) return
    try {
      const payload = await updateOrder(selectedOrder._id, {
        fulfillment_status: statusDraft,
        notes: notesDraft
      })
      setSelectedOrder(payload.data)
      setActionMessage(
        statusDraft === 'out_for_delivery'
          ? 'Comanda a fost mutată în livrare. Documentele disponibile au fost publicate pentru client și emailul a fost trimis.'
          : 'Comanda a fost actualizată.'
      )
      setActionSeverity('success')
      await loadOrders(filters, showClosedOrders)
    } catch (actionError) {
      setActionMessage(actionError.message)
      setActionSeverity('error')
    }
  }

  const handleResend = async () => {
    if (!selectedOrder) return
    try {
      await resendConfirmation(selectedOrder._id)
      setActionMessage('Emailul a fost retrimis.')
      setActionSeverity('success')
    } catch (actionError) {
      setActionMessage(actionError.message)
      setActionSeverity('error')
    }
  }

  const handleFinalInvoiceUpload = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!selectedOrder || !file) return

    setInvoiceUploading(true)
    setActionMessage('')
    try {
      const payload = await uploadFinalInvoice(selectedOrder._id, file)
      setSelectedOrder(payload.data)
      setActionMessage('Factura finala Primusvest a fost publicata pentru client.')
      setActionSeverity('success')
      await loadOrders(filters, showClosedOrders)
    } catch (actionError) {
      setActionMessage(actionError.message)
      setActionSeverity('error')
    } finally {
      setInvoiceUploading(false)
    }
  }

  const handleQuickStatusChange = (orderId, value) => {
    setRowStatusDrafts(currentDrafts => ({
      ...currentDrafts,
      [orderId]: value
    }))
  }

  const handleQuickStatusSave = async (orderId) => {
    const nextStatus = rowStatusDrafts[orderId]
    if (!nextStatus) {
      return
    }
    setRowActionOrderId(orderId)
    setActionMessage('')
    try {
      await updateOrder(orderId, { fulfillment_status: nextStatus })
      setActionMessage(
        nextStatus === 'out_for_delivery'
          ? 'Comanda selectată a fost mutată în livrare. Documentele disponibile au fost publicate pentru client.'
          : 'Statusul comenzii a fost actualizat.'
      )
      setActionSeverity('success')
      if (selectedOrder?._id === orderId) {
        const payload = await getOrder(orderId)
        setSelectedOrder(payload.data)
        setStatusDraft(payload.data.fulfillment_status || 'new')
      }
      await loadOrders(filters, showClosedOrders)
    } catch (actionError) {
      setError(actionError.message)
      setActionMessage(actionError.message)
      setActionSeverity('error')
    } finally {
      setRowActionOrderId('')
    }
  }

  const detailRows = useMemo(() => selectedOrder?.items || [], [selectedOrder])

  const handleBankTransferAction = async (action) => {
    if (!selectedOrder) return
    try {
      const payload = await updateOrder(selectedOrder._id, { bank_transfer_action: action })
      setSelectedOrder(payload.data)
      setActionMessage(
        action === 'confirm'
          ? 'Plata prin ordin de plata a fost confirmata.'
          : 'Dovada platii a fost respinsa.'
      )
      setActionSeverity(action === 'confirm' ? 'success' : 'warning')
      await loadOrders(filters, showClosedOrders)
    } catch (actionError) {
      setActionMessage(actionError.message)
      setActionSeverity('error')
    }
  }

  return (
    <>
      <Typography variant='h6' sx={{ marginBottom: '20px' }}>Comenzi</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ marginBottom: '20px' }}>
        <TextField
          size='small'
          label='Caută după comandă / email / CUI'
          value={filters.search}
          onChange={(event) => handleFilterChange('search', event.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ minWidth: 320 }}
        />
        <TextField
          size='small'
          select
          label='Status plată'
          value={filters.payment_status}
          onChange={(event) => handleFilterChange('payment_status', event.target.value)}
          sx={{ minWidth: 220 }}
        >
          {paymentOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </TextField>
        <TextField
          size='small'
          select
          label='Status procesare'
          value={filters.fulfillment_status}
          onChange={(event) => handleFilterChange('fulfillment_status', event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {fulfillmentOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </TextField>
        <Button variant='contained' color='drawer' sx={{ color: 'white' }} onClick={applyFilters}>Aplică</Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent='space-between'
        sx={{ marginBottom: '16px' }}
      >
        <FormControlLabel
          sx={{
            margin: 0,
            '& .MuiFormControlLabel-label': {
              color: theme.palette.fontColor.dark,
              fontWeight: 500
            }
          }}
          control={(
            <Checkbox
              checked={showClosedOrders}
              sx={{
                color: theme.palette.drawer.main,
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: 24
                },
                '&.Mui-checked': {
                  color: theme.palette.accent.main
                }
              }}
              onChange={(event) => {
                const nextValue = event.target.checked
                setShowClosedOrders(nextValue)
                loadOrders(filters, nextValue)
              }}
            />
          )}
          label='Include finalizate și anulate'
        />
        <Typography variant='body2' color='text.secondary'>
          Implicit afișăm doar comenzile active. Încarcă factura finală Primusvest când este disponibilă; mutarea în „În livrare” publică documentul Stripe unde există și trimite emailul de livrare.
        </Typography>
      </Stack>

      {actionMessage && <Alert severity={actionSeverity} sx={{ marginBottom: '16px' }}>{actionMessage}</Alert>}

      {loading && <Typography sx={{ marginBottom: '12px' }}>Se încarcă comenzile…</Typography>}
      {!loading && !error && orders.length === 0 && (
        <Typography sx={{ marginBottom: '12px' }}>Nu există comenzi încă.</Typography>
      )}
      {error && <Typography color='error' sx={{ marginBottom: '12px' }}>{error}</Typography>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Comandă</TableCell>
              <TableCell>Dată</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>CUI</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Plată</TableCell>
              <TableCell>Procesare</TableCell>
              <TableCell>Stoc</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map(order => (
              <TableRow key={order._id}>
                <TableCell title={order.order_number}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                      {shortenOrderNumber(order.order_number)}
                    </Typography>
                    {shortenOrderNumber(order.order_number) !== order.order_number && (
                      <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium }}>
                        {order.order_number}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{new Date(order.created_at).toLocaleString('ro-RO')}</TableCell>
                <TableCell>{order.company_snapshot?.company || order.customer_snapshot?.email}</TableCell>
                <TableCell>{order.company_snapshot?.cui || '-'}</TableCell>
                <TableCell>{order.total_price} RON</TableCell>
                <TableCell>{paymentLabels[order.payment_status] || order.payment_status}</TableCell>
                <TableCell>{fulfillmentLabels[order.fulfillment_status] || order.fulfillment_status}</TableCell>
                <TableCell>
                  <Chip
                    label={readinessLabels[order.fulfillment_readiness] || order.fulfillment_readiness}
                    color={readinessColors[order.fulfillment_readiness] || 'default'}
                    size='small'
                  />
                </TableCell>
                <TableCell>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1}>
                    <TextField
                      select
                      size='small'
                      label='Procesare'
                      value={rowStatusDrafts[order._id] || order.fulfillment_status || 'new'}
                      onChange={(event) => handleQuickStatusChange(order._id, event.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {fulfillmentOptions.filter(option => option.value).map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant='contained'
                      color='drawer'
                      size='small'
                      sx={{ color: 'white' }}
                      disabled={
                        rowActionOrderId === order._id ||
                        (rowStatusDrafts[order._id] || order.fulfillment_status || 'new') === order.fulfillment_status
                      }
                      onClick={() => handleQuickStatusSave(order._id)}
                    >
                      {rowActionOrderId === order._id ? 'Salvăm…' : 'Actualizează'}
                    </Button>
                    <Button variant='outlined' size='small' onClick={() => openDetails(order._id)}>
                      Detalii
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle>Detalii comandă</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Stack spacing={3}>
              <Box>
                <Typography><strong>Comandă:</strong> {shortenOrderNumber(selectedOrder.order_number)}</Typography>
                {shortenOrderNumber(selectedOrder.order_number) !== selectedOrder.order_number && (
                  <Typography variant='caption' sx={{ display: 'block', color: theme.palette.fontColor.medium }}>
                    Ref completă: {selectedOrder.order_number}
                  </Typography>
                )}
                <Typography><strong>Companie:</strong> {selectedOrder.company_snapshot?.company}</Typography>
                <Typography><strong>CUI:</strong> {selectedOrder.company_snapshot?.cui}</Typography>
                <Typography><strong>Facturare:</strong> {selectedOrder.billing_snapshot?.full_address}</Typography>
                <Typography><strong>Livrare:</strong> {selectedOrder.delivery_snapshot?.full_address}</Typography>
                <Typography><strong>Metodă plată:</strong> {selectedOrder.payment_method === 'bank_transfer' ? 'Ordin de plată' : 'Card online'}</Typography>
                {selectedOrder.payment_method === 'bank_transfer' && (
                  <Typography><strong>Status OP:</strong> {bankTransferLabels[selectedOrder.bank_transfer_status] || selectedOrder.bank_transfer_status}</Typography>
                )}
                <Typography>
                  <strong>Factură client:</strong> {
                    selectedOrder.final_invoice
                      ? 'Factura finală Primusvest este disponibilă în contul clientului'
                      : selectedOrder.customer_invoice_available
                        ? 'Documentul Stripe este disponibil în contul clientului'
                        : 'Ascunsă până la livrare'
                  }
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  size='small'
                  label='Status procesare'
                  value={statusDraft}
                  onChange={(event) => setStatusDraft(event.target.value)}
                  sx={{ minWidth: 280 }}
                >
                  {fulfillmentOptions.filter(option => option.value).map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size='small'
                  label='Notițe'
                  value={notesDraft}
                  onChange={(event) => setNotesDraft(event.target.value)}
                  fullWidth
                />
              </Stack>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Produs</TableCell>
                    <TableCell>Cant.</TableCell>
                    <TableCell>Preț</TableCell>
                    <TableCell>Stoc</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.map(item => (
                    <TableRow key={item.item_id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.effective_price} RON</TableCell>
                      <TableCell>{item.stock_label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {selectedOrder.payment_method === 'bank_transfer' && selectedOrder.bank_transfer_proof_download_url && (
                  <Button component='a' href={selectedOrder.bank_transfer_proof_download_url} target='_blank' rel='noreferrer' variant='outlined'>
                    Vezi dovada platii
                  </Button>
                )}
                {selectedOrder.payment_method === 'bank_transfer' && (
                  <Button
                    onClick={() => handleBankTransferAction('confirm')}
                    variant='contained'
                    color='success'
                    disabled={selectedOrder.payment_status === 'paid' || !selectedOrder.bank_transfer_proof_download_url}
                  >
                    Confirma OP
                  </Button>
                )}
                {selectedOrder.payment_method === 'bank_transfer' && (
                  <Button
                    onClick={() => handleBankTransferAction('reject')}
                    variant='outlined'
                    color='warning'
                    disabled={!selectedOrder.bank_transfer_proof_download_url}
                  >
                    Respinge dovada
                  </Button>
                )}
                <Button component='label' variant='contained' color='drawer' sx={{ color: 'white' }} disabled={invoiceUploading || selectedOrder.payment_status !== 'paid'}>
                  {selectedOrder.final_invoice ? 'Inlocuieste factura finala PDF' : 'Incarca factura finala PDF'}
                  <input hidden accept='application/pdf,.pdf' type='file' onChange={handleFinalInvoiceUpload} />
                </Button>
                {selectedOrder.final_invoice_download_url && (
                  <Button component='a' href={selectedOrder.final_invoice_download_url} target='_blank' rel='noreferrer' variant='outlined'>
                    Vezi factura Primusvest
                  </Button>
                )}
                {selectedOrder.customer_invoice_available && selectedOrder.stripe_invoice_url && (
                  <Button component='a' href={selectedOrder.stripe_invoice_url} target='_blank' rel='noreferrer' variant='outlined'>
                    Document Stripe
                  </Button>
                )}
                {selectedOrder.customer_invoice_available && selectedOrder.stripe_invoice_pdf && (
                  <Button component='a' href={selectedOrder.stripe_invoice_pdf} target='_blank' rel='noreferrer' variant='outlined'>
                    PDF Stripe
                  </Button>
                )}
                {selectedOrder.stripe_receipt_url && (
                  <Button component='a' href={selectedOrder.stripe_receipt_url} target='_blank' rel='noreferrer' variant='outlined'>
                    Chitanță plată
                  </Button>
                )}
              </Stack>

              {actionMessage && <Typography color='success.main'>{actionMessage}</Typography>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResend} variant='outlined'>
            {selectedOrder?.final_invoice
              ? 'Trimite/retrimite factura finala'
              : selectedOrder?.customer_invoice_available
                ? 'Retrimite email livrare/document Stripe'
                : 'Retrimite confirmarea plății'}
          </Button>
          <Button onClick={handleStatusSave} variant='contained' color='drawer' sx={{ color: 'white' }}>Salvează</Button>
          <Button onClick={() => setDetailsOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Orders
